import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FiX, FiImage, FiEdit2, FiMinus } from "react-icons/fi";
import { AlertMsg } from "../../../shared/components/alert-msg";
import ImageDrawer from "../../../shared/components/media/ImageDrawer";
import MediaSelectionPreview from "../../../shared/components/media/MediaSelectionPreview";
import { MediaPageProps } from "../../../types/app/mediaTypes";
import "./AddProductCategoryModal.scss";

interface AddProductCategoryModalProps {
    show: boolean;
    onClose: () => void;
    handleCreateCategory?: (payload: {
        name: string;
    }) => void
    handleUpdateCategory?: (payload: {
        name: string;
        id: number;
    }) => void;
    id?: number;
    type?: "create" | "update"
    initValues?: { name: string, description: string, icon: string | null }
    isSubmitting?: boolean;
    submitError?: string;
    mediaPageProps: Pick<
        MediaPageProps,
        "getImageGalleryAction" | "getImageGalleryRequest"
    >;
}

const validationSchema = Yup.object().shape({
    name: Yup.string().required("Category name is required"),
    description: Yup.string().max(250, "Maximum 250 characters"),
    icon: Yup.mixed().nullable(),
});

export const AddProductCategoryModal: React.FC<AddProductCategoryModalProps> = ({
    show,
    onClose,
    handleCreateCategory,
    type = "create",
    initValues,
    id,
    handleUpdateCategory,
    isSubmitting = false,
    submitError = "",
    mediaPageProps
}) => {
    const [showImageDrawer, setShowImageDrawer] = React.useState(false);

    if (!show) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-container">
                {/* Header */}
                <div className="modal-header">
                    <h2>{type === "create" ? "Add new" : "Update"} category</h2>
                    <FiX className="close-icon" onClick={onClose} />
                </div>

                {/* Form */}
                <Formik
                    enableReinitialize
                    initialValues={initValues ?? {
                        name: "",
                        description: "",
                        icon: null,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        if (type === "create" && handleCreateCategory) {
                            handleCreateCategory({ name: values.name })
                        } else if (type === "update" && id && handleUpdateCategory) {
                            handleUpdateCategory({ id, name: values.name })
                        }
                        // onClose();
                    }}
                >
                    {({ setFieldValue, values }) => (
                        <Form className="modal-form">
                            {submitError &&
                                <AlertMsg type="error" message={submitError} />
                            }
                            {/* Product name */}
                            <label>Product name</label>
                            <Field
                                name="name"
                                placeholder="Enter category name"
                                className="input-field"
                            />
                            <span className="helper-text-url">
                                https://www.mamimarkets.com/category/
                                {values.name.toLowerCase().replace(/\s+/g, "-") || ""}
                            </span>
                            <ErrorMessage
                                name="name"
                                component="div"
                                className="error-text"
                            />

                            {/* Description */}
                            <label>Description</label>
                            <Field
                                as="textarea"
                                name="description"
                                className="textarea-field"
                                rows={3}
                            />
                            <span className="helper-text">Maximum of 250 characters</span>
                            <ErrorMessage
                                name="description"
                                component="div"
                                className="error-text"
                            />

                            {/* Category icon */}
                            <label>Category Icon</label>
                            {values.icon ? (
                                <div className="icon-preview">
                                    <MediaSelectionPreview
                                        imageUrl={typeof values.icon === "string" ? values.icon : ""}
                                        name={values.name || "Category"}
                                        className="uploaded-icon"
                                    />
                                    <button
                                        type="button"
                                        className="edit-btn"
                                        onClick={() => setShowImageDrawer(true)}
                                    >
                                        <FiEdit2 size={30} />
                                    </button>
                                    <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={() => setFieldValue("icon", null)}
                                    >
                                        <FiMinus size={16} />
                                    </button>
                                </div>
                            ) : (
                                <button type="button" className="upload-box" onClick={() => setShowImageDrawer(true)}>
                                    <FiImage size={24} strokeWidth={3} />
                                </button>
                            )}

                            {/* Submit button */}
                            <button type="submit" className="submit-btn" disabled={isSubmitting}>
                                {type === "create" ? !isSubmitting ? "Add new" : "Adding new" : isSubmitting ? "Updating" : "Update"} category
                            </button>

                            <ImageDrawer
                                show={showImageDrawer}
                                onClose={() => setShowImageDrawer(false)}
                                onSelect={(imageUrl) => {
                                    setFieldValue("icon", imageUrl);
                                    setShowImageDrawer(false);
                                }}
                                currentImageUrl={typeof values.icon === "string" ? values.icon : ""}
                                currentName={values.name || "Category"}
                                title="Choose category icon"
                                subtitle="Select an image from your media library."
                                confirmLabel="Use image"
                                pageProps={mediaPageProps}
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};
